import { APP_ROUTES } from "~/constants/appRoutes";
import { PRODUCT } from "~/constants/products";

// FX event keys for the eight Optimizely Web GTM tags. Note the quiz-start
// keys differ from their Web names (start_quiz_oestra).
export const FX_EVENT_NAMES = {
    LP_TO_QUIZ: "lp_to_quiz",
    PQP_TO_QUIZ: "pqp_to_quiz",
    PQP_TO_CHECKOUT: "pqp_to_checkout",
    QUIZ_TO_CHECKOUT: "quiz_to_checkout",
    START_QUIZ_OESTRA: "start_quiz_-_oestra",
    START_QUIZ_LIBIDA: "start_quiz_-_libida",
    QUIZ_COMPLETED: "quiz_completed",
    PURCHASE: "purchase",
} as const;

export type FxEventName = typeof FX_EVENT_NAMES[keyof typeof FX_EVENT_NAMES];
export type FxEventProperties = Record<string, string | number | boolean>;

export type FxEventPayload = {
    event_name: FxEventName | undefined;
    value?: number;
    /** Integer amount in cents, for Optimizely revenue metrics. */
    revenue?: number;
    properties?: FxEventProperties;
};

const ALLOWED = new Set<string>(Object.values(FX_EVENT_NAMES));

export function isFxEventName(eventName: unknown): eventName is FxEventName {
    return typeof eventName === "string" && ALLOWED.has(eventName);
}

// Path fragments the funnel events match on, copied from their Web GTM tags.
// PQP_CHECKOUT ("/pqp") is broader than PQP_QUIZ ("pqp/"): it also matches
// /pqp-upsell and the /pqp-<product> funnel pages.
export const FX_FUNNEL_PATHS = {
    PQP_CHECKOUT: "/pqp",
    PQP_QUIZ: "pqp/",
    QUIZ: "quiz/",
} as const;

// Face-cream and nad have no quiz-start tag in Web.
const START_QUIZ_EVENTS = new Map<string, FxEventName>([
    [PRODUCT.OESTRA_CREAM, FX_EVENT_NAMES.START_QUIZ_OESTRA],
    [PRODUCT.LIBIDA, FX_EVENT_NAMES.START_QUIZ_LIBIDA],
]);

export function startQuizFxEvent(productName: string): FxEventName | undefined {
    return START_QUIZ_EVENTS.get(productName);
}

export function fxItemProperties(items?: { item_id?: string; item_slug?: string }[]): FxEventProperties {
    return { itemId: items?.[0]?.item_id ?? "", itemSlug: items?.[0]?.item_slug ?? "" };
}

// Landing pages feeding lp_to_quiz. Exact paths, not a "/p/" substring: only
// the nad LP lives under /p/, and a substring also matches third-party
// referrers (instagram.com/p/<id>, facebook.com/p/<name>).
const LANDING_PAGES = new Set<string>([
    APP_ROUTES.TREATMENT_HRT,
    APP_ROUTES.TREATMENT_ENDOMETRIOSIS,
    APP_ROUTES.TREATMENT_PERIMENOPAUSE,
    APP_ROUTES.TREATMENT_MENOPAUSE,
    APP_ROUTES.TREATMENT_PCOS,
    APP_ROUTES.TREATMENT_POSTPARTUM,
    APP_ROUTES.LIBIDA_LP,
    APP_ROUTES.NAD_LP,
]);

export function isFxLandingPage(path: string): boolean {
    return LANDING_PAGES.has(path.endsWith("/") ? path.slice(0, -1) : path);
}
